// Pure workflow cost estimation: formula evaluation + margin math.
//
// Extracted from ImageGenerationContext.calculateTokenCost so it can be reused
// by the Multi (parallel multi-model) section, where each checked model needs a
// per-row estimate. This module is intentionally free of React/DOM/refs — the
// caller collects widget values and passes them in; side effects (e.g. writing a
// discount-info ref) stay in the caller, which reads the returned `discountInfo`.
//
// Sentinel semantics are preserved verbatim from the original implementation:
//   -2 requires images, -3 missing params (widgets still loading),
//   -4 requires audio, -5 requires video, -6 requires audio or video,
//   -7 requires prompt, -8 requires text, -9 requires any of _may_zero inputs,
//   null error.

import { calculateTokenCostDisplay, formulaRequiresInputImages } from './tokenCostDisplay';
import { FormulaEvaluator } from '@functions/utils/formula-evaluator';
import type { ImageDimensions } from '@functions/utils/image-dimensions';
import { buildMultiselectCountContext } from '@functions/utils/multiselectCounts';

// Shared with missing-input hints so mandatory and optional duration variants stay aligned.
export const MEDIA_DURATION_FORMULA_PATTERNS = {
  requiredAudio: /\baudio_duration(?:_exact)?\b(?!_may_zero)/,
  requiredVideo: /\bvideo_duration(?:_exact)?\b(?!_may_zero)/,
  optionalAudio: /\baudio_duration(?:_exact)?_may_zero\b/,
  optionalVideo: /\bvideo_duration(?:_exact)?_may_zero\b/,
} as const;

export interface TokenCostResult {
  totalCost: number;
  minCost?: number;           // only for multi-file
  maxCost?: number;           // only for multi-file
  requestCount: number;
  perRequestCost?: number;    // for multi-generation (num_images > 1)
  perRequestCosts?: number[]; // for multi-file
  isMultiGeneration?: boolean; // distinguish from multi-file
}

export interface WorkflowCostInfo {
  pricing_formula?: string | null;
  input_files_logic?: string | null;
  margin_percent?: number | null;
  title?: string;
}

export interface EstimateWorkflowCostOptions {
  /** Dimensions of provided input images (drives requiresInputImages + single_per_request). */
  inputImageDimensions: ImageDimensions[];
  /** Explicit image ownership used when Slots counters are narrowed per request. */
  slotsInputImageDimensions?: ImageDimensions[];
  standardInputImageDimensions?: ImageDimensions[];
  /** True when image inputs are selected but browser dimension probing is still unavailable. */
  hasInputImages?: boolean;
  /** Target output dimensions extracted from image_size widget fields. */
  targetImageDimensions: ImageDimensions | null | undefined;
  /** Org-level margin discount (percentage points subtracted from margin_percent). */
  marginDiscount: number;
  /** Widget definition used to derive trusted multiselect pricing counts. */
  dynamicWidget?: unknown;
}

export interface EstimateWorkflowCostReturn {
  /** Cost result, a negative sentinel number, or null on evaluation error. */
  result: TokenCostResult | number | null;
  /** Full (pre-discount) price for tooltip display, or null when no discount applies. */
  discountInfo: { fullPrice: number } | null;
}

const SLOT_COUNTERS_BY_MEDIA_TYPE = [
  ['image', 'slots_image_count'],
  ['video', 'slots_video_count'],
  ['audio', 'slots_audio_count'],
  ['model', 'slots_model_count'],
] as const;

interface SinglePerRequestPricingInput {
  pricingContext: Record<string, any>;
  inputImageDimensions: ImageDimensions[];
  repeatCount: number;
}

// `perRequestCosts` is diagnostic detail only; the UI reads total/min/max/count.
// Keep the detail for ordinary fan-out without allowing a raw counter to allocate
// an arbitrarily large array on every pricing poll.
const MAX_DETAILED_PER_REQUEST_COSTS = 100;

/**
 * Mirror the Slots counter narrowing performed by narrowPayloadForSlot before
 * each single_per_request API call. Image dimensions already describe only
 * image inputs, while Slots counters also let us account for video, audio, and
 * model requests that have no ImageDimensions entry.
 */
function buildSinglePerRequestPricingInputs(
  pricingContext: Record<string, any>,
  inputImageDimensions: ImageDimensions[],
  slotsInputImageDimensions?: ImageDimensions[],
  standardInputImageDimensions?: ImageDimensions[]
): SinglePerRequestPricingInput[] {
  const presentCounters = SLOT_COUNTERS_BY_MEDIA_TYPE.filter(([, key]) =>
    Object.prototype.hasOwnProperty.call(pricingContext, key)
  );
  const slotRequestCounts = presentCounters.map(([mediaType, key]) => {
    const count = Number(pricingContext[key]);
    const normalizedCount = Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
    return { mediaType, count: normalizedCount };
  });
  const slotRequestCount = slotRequestCounts.reduce((total, item) => total + item.count, 0);

  // Workflows without Slots counters retain the established image-only path.
  if (slotRequestCount === 0) {
    return inputImageDimensions.map(imageDimensions => ({
      pricingContext,
      inputImageDimensions: [imageDimensions],
      repeatCount: 1,
    }));
  }

  const slotsImageCount = slotRequestCounts.find(item => item.mediaType === 'image')?.count ?? 0;
  const hasExplicitImageProvenance =
    slotsInputImageDimensions !== undefined || standardInputImageDimensions !== undefined;
  const slotImageDimensions = hasExplicitImageProvenance
    ? (slotsInputImageDimensions ?? [])
    : inputImageDimensions.slice(0, slotsImageCount);
  const nonSlotImageDimensions = hasExplicitImageProvenance
    ? (standardInputImageDimensions ?? [])
    : inputImageDimensions.slice(slotsImageCount);

  const buildRequestContext = (mediaType?: typeof SLOT_COUNTERS_BY_MEDIA_TYPE[number][0]) => {
    const requestContext = { ...pricingContext };
    presentCounters.forEach(([, key]) => { requestContext[key] = 0; });
    const selectedCounter = SLOT_COUNTERS_BY_MEDIA_TYPE.find(([type]) => type === mediaType)?.[1];
    if (selectedCounter) requestContext[selectedCounter] = 1;
    return requestContext;
  };

  const requests: SinglePerRequestPricingInput[] = [];
  for (const { mediaType, count } of slotRequestCounts) {
    if (count === 0) continue;

    // Image requests can have different formula inputs, so price each available
    // dimensions record once. Any excess counter entries are identical no-dimension
    // requests and can be represented as one repeated group.
    if (mediaType === 'image') {
      const dimensionsToConsume = Math.min(count, slotImageDimensions.length);
      for (let imageDimensionIndex = 0; imageDimensionIndex < dimensionsToConsume; imageDimensionIndex++) {
        requests.push({
          pricingContext: buildRequestContext(mediaType),
          inputImageDimensions: [slotImageDimensions[imageDimensionIndex]!],
          repeatCount: 1,
        });
      }
      if (count > dimensionsToConsume) {
        requests.push({
          pricingContext: buildRequestContext(mediaType),
          inputImageDimensions: [],
          repeatCount: count - dimensionsToConsume,
        });
      }
      continue;
    }

    // Video/audio/model requests have no per-item pricing metadata in this
    // payload, so every request of a given type has the same evaluated cost.
    requests.push({
      pricingContext: buildRequestContext(mediaType),
      inputImageDimensions: [],
      repeatCount: count,
    });
  }

  // Images supplied by non-Slots widgets still fan out into their own requests.
  // narrowPayloadForSlot preserves the counter identifiers for those requests,
  // but sets every one to zero because the selected media does not belong to Slots.
  for (const imageDimensions of nonSlotImageDimensions) {
    requests.push({
      pricingContext: buildRequestContext(),
      inputImageDimensions: [imageDimensions],
      repeatCount: 1,
    });
  }

  return requests;
}

/**
 * Evaluate a workflow's pricing formula with the given context and apply margin.
 *
 * Assumes the caller has already handled widget-loading states (returning -1).
 * `context` must already include the merged widget payload values (num_images,
 * audio_duration, video_duration, video_mpx, prompt, text, etc.).
 */
export function estimateWorkflowCost(
  workflow: WorkflowCostInfo,
  context: Record<string, any>,
  opts: EstimateWorkflowCostOptions
): EstimateWorkflowCostReturn {
  const noDiscount: EstimateWorkflowCostReturn = { result: null, discountInfo: null };

  const pricingFormula = workflow?.pricing_formula;
  if (!pricingFormula) {
    return { result: 100, discountInfo: null }; // Default cost
  }

  const { inputImageDimensions, targetImageDimensions, marginDiscount } = opts;
  const pricingContext = {
    ...context,
    ...buildMultiselectCountContext(context, opts.dynamicWidget)
  };

  // Check if formula requires input images or media duration
  // (input_img_mpx / input_img_count are mandatory; input_img_count_may_zero is optional)
  const requiresInputImages = formulaRequiresInputImages(pricingFormula);
  // Recognize rounded and exact durations without treating optional variants as mandatory
  const requiresAudioDuration = MEDIA_DURATION_FORMULA_PATTERNS.requiredAudio.test(pricingFormula);
  const requiresVideoDuration = MEDIA_DURATION_FORMULA_PATTERNS.requiredVideo.test(pricingFormula);
  // Detect formulas that require video dimension data (video_mpx, video_width, video_height)
  const requiresVideoMedia = /\b(video_mpx|video_width|video_height)\b/.test(pricingFormula);
  // Detect optional media variables (_may_zero variants require at least one media)
  // But if has_audio/has_video guards are present, the formula handles absence via ternary — don't require media
  const hasOptionalAudio = MEDIA_DURATION_FORMULA_PATTERNS.optionalAudio.test(pricingFormula);
  const hasOptionalVideo = MEDIA_DURATION_FORMULA_PATTERNS.optionalVideo.test(pricingFormula);
  const hasAudioGuard = /\bhas_audio\b/.test(pricingFormula);
  const hasVideoGuard = /\bhas_video\b/.test(pricingFormula);
  // Detect mandatory prompt/text length parameters (not _may_zero variants)
  const requiresPromptLength = /\b(prompt_length_ceil_100|prompt_length_1k)\b(?!_may_zero)/.test(pricingFormula);
  const requiresTextLength = /\btext_length_ceil_100\b(?!_may_zero)/.test(pricingFormula);
  // Detect optional prompt/text _may_zero parameters
  const hasOptionalPrompt = /\b(prompt_length_ceil_100|prompt_length_1k)_may_zero\b/.test(pricingFormula);
  const hasOptionalText = /\btext_length_ceil_100_may_zero\b/.test(pricingFormula);

  try {
    // Check if formula requires input images and none are provided
    if (requiresInputImages && inputImageDimensions.length === 0) {
      // A selected image that the browser cannot decode (for example HEIC or
      // a CORS-restricted reference) must still reach authoritative server
      // measurement. Keep the preview in a non-blocking "calculating" state.
      return { ...noDiscount, result: opts.hasInputImages ? -1 : -2 };
    }

    // Check if formula requires audio_duration but none provided
    if (requiresAudioDuration && !context?.audio_duration) {
      return { ...noDiscount, result: -4 }; // Requires audio
    }

    // Check if formula requires video_duration but none provided
    if (requiresVideoDuration && !context?.video_duration) {
      return { ...noDiscount, result: -5 }; // Requires video
    }

    // Check if formula requires video dimensions (video_mpx) but none provided
    if (requiresVideoMedia && !context?.video_mpx) {
      return { ...noDiscount, result: -5 }; // Requires video
    }

    // Check if formula uses optional media variables but no media provided at all
    // Skip if has_audio/has_video guards are present — the formula handles absence via ternary
    const needsOptionalAudio = hasOptionalAudio && !hasAudioGuard;
    const needsOptionalVideo = hasOptionalVideo && !hasVideoGuard;
    if ((needsOptionalAudio || needsOptionalVideo) && !context?.audio_duration && !context?.video_duration) {
      return { ...noDiscount, result: -6 }; // Requires at least audio or video
    }

    // Check if formula requires prompt_length_ceil_100 but no prompt provided
    if (requiresPromptLength && !context?.prompt?.trim()) {
      return { ...noDiscount, result: -7 }; // Requires prompt
    }

    // Check if formula requires text_length_ceil_100 but no text provided
    if (requiresTextLength && !context?.text?.trim()) {
      return { ...noDiscount, result: -8 }; // Requires text
    }

    // Check if formula uses multiple _may_zero params (from different categories) and ALL are empty
    // e.g. has_audio > 0 ? audio_duration_may_zero * 110 : prompt_length_ceil_100_may_zero * 660
    // When neither audio nor prompt provided → result is 0, show "provide one of..." message
    {
      const mayZeroProvided: boolean[] = [];
      if (hasOptionalAudio) mayZeroProvided.push(!!context?.audio_duration);
      if (hasOptionalVideo) mayZeroProvided.push(!!context?.video_duration);
      if (hasOptionalPrompt) mayZeroProvided.push(!!context?.prompt?.trim());
      if (hasOptionalText) mayZeroProvided.push(!!context?.text?.trim());
      // If 2+ _may_zero categories and NONE have values → need at least one
      if (mayZeroProvided.length >= 2 && !mayZeroProvided.some(Boolean)) {
        return { ...noDiscount, result: -9 }; // Requires at least one of the _may_zero inputs
      }
    }

    // Check if workflow uses single_per_request mode (each file is separate request)
    const isSinglePerRequest = workflow?.input_files_logic === 'single_per_request';
    const perRequestPricingInputs = isSinglePerRequest
      ? buildSinglePerRequestPricingInputs(
          pricingContext,
          inputImageDimensions,
          opts.slotsInputImageDimensions,
          opts.standardInputImageDimensions
        )
      : [];
    const singlePerRequestCount = perRequestPricingInputs.reduce(
      (total, input) => total + input.repeatCount,
      0
    );
    const hasMultipleFiles = singlePerRequestCount > 1;

    // Calculate cost differently based on file processing mode
    if (isSinglePerRequest && hasMultipleFiles) {
      // Calculate cost for EACH file separately
      const includeDetailedCosts = singlePerRequestCount <= MAX_DETAILED_PER_REQUEST_COSTS;
      const perRequestCosts: number[] | undefined = includeDetailedCosts ? [] : undefined;
      let totalCost = 0;
      let minCost = Number.POSITIVE_INFINITY;
      let maxCost = Number.NEGATIVE_INFINITY;
      let fullTotalCost = 0;

      for (const requestInput of perRequestPricingInputs) {
        const customInput = {
          inputImageDimensions: requestInput.inputImageDimensions,
          targetImageDimensions: targetImageDimensions
        };

        const baseCost = FormulaEvaluator.evaluate(pricingFormula, requestInput.pricingContext, customInput);

        const marginPercent = (workflow as any).margin_percent ?? 50;
        const effectiveMargin = Math.max(0, marginPercent - marginDiscount);
        const finalCost = effectiveMargin >= 99
          ? Math.ceil(baseCost * 100)
          : Math.ceil(baseCost / (1 - effectiveMargin / 100));
        totalCost += finalCost * requestInput.repeatCount;
        minCost = Math.min(minCost, finalCost);
        maxCost = Math.max(maxCost, finalCost);
        if (perRequestCosts) {
          for (let i = 0; i < requestInput.repeatCount; i++) {
            perRequestCosts.push(finalCost);
          }
        }

        // Compute full price (without discount) for tooltip
        if (marginDiscount > 0) {
          const fullCost = marginPercent >= 99
            ? Math.ceil(baseCost * 100)
            : Math.ceil(baseCost / (1 - marginPercent / 100));
          fullTotalCost += fullCost * requestInput.repeatCount;
        }
      }

      // Compute discount info for tooltip
      let discountInfo: { fullPrice: number } | null = null;
      if (marginDiscount > 0) {
        discountInfo = { fullPrice: fullTotalCost };
      }

      return {
        result: {
          totalCost,
          minCost,
          maxCost,
          requestCount: singlePerRequestCount,
          ...(perRequestCosts ? { perRequestCosts } : {})
        },
        discountInfo
      };
    } else {
      // Standard calculation (all files in one request or no files)
      const customInput = {
        inputImageDimensions: inputImageDimensions,
        targetImageDimensions: targetImageDimensions
      };

      // Evaluate formula with context and custom parameters
      const baseCost = FormulaEvaluator.evaluate(pricingFormula, pricingContext, customInput);

      const marginPercent = (workflow as any).margin_percent ?? 50;
      const effectiveMargin = Math.max(0, marginPercent - marginDiscount);
      const finalCost = effectiveMargin >= 99
        ? Math.ceil(baseCost * 100)
        : Math.ceil(baseCost / (1 - effectiveMargin / 100));

      // Compute full price (without discount) for tooltip
      let discountInfo: { fullPrice: number } | null = null;
      if (marginDiscount > 0) {
        const fullPrice = marginPercent >= 99
          ? Math.ceil(baseCost * 100)
          : Math.ceil(baseCost / (1 - marginPercent / 100));
        const numGenerations = context.num_images || 1;
        const formulaIncludesNumImages = pricingFormula.includes('num_images') || pricingFormula.includes('gen_count');
        const fullTotalPrice = numGenerations > 1 && !formulaIncludesNumImages
          ? fullPrice * numGenerations
          : fullPrice;
        discountInfo = { fullPrice: fullTotalPrice };
      }

      // Ensure minimum cost of 1 token
      if (finalCost < 1) {
        return { result: null, discountInfo: null }; // Error: invalid result
      }

      // Calculate display cost (handles multi-generation scenarios)
      const numGenerations = context.num_images || 1;
      return {
        result: calculateTokenCostDisplay(finalCost, numGenerations, pricingFormula),
        discountInfo
      };
    }
  } catch (error: any) {
    // Check if error is due to missing parameters (widgets not loaded yet)
    if (error.message?.includes('contains identifier')) {
      return { ...noDiscount, result: -3 }; // Missing parameters - widgets still loading
    }

    console.error('❌ Error calculating token cost:', {
      workflow: workflow.title,
      formula: pricingFormula,
      error: error.message
    });
    return { result: null, discountInfo: null }; // Error: formula evaluation failed
  }
}
